import React, { useState } from 'react';
import { MessageCircle, Search, Menu, X, ChevronRight, Book, Settings, Zap, HelpCircle, Code } from 'lucide-react';

const DocsLayout = ({ children, currentPage }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    {
      title: 'Getting Started',
      icon: Book,
      items: [
        { name: 'Quick Start', href: '/docs/quick-start' },
        { name: 'Account Setup', href: '/docs/account-setup' },
        { name: 'First Ticket', href: '/docs/first-ticket' },
      ]
    },
    {
      title: 'Integrations',
      icon: Zap,
      items: [
        { name: 'Gmail Setup', href: '/docs/gmail-setup' },
        { name: 'Facebook Messenger', href: '/docs/facebook-setup' },
        { name: 'WhatsApp Business', href: '/docs/whatsapp-setup' },
        { name: 'Trustpilot Monitoring', href: '/docs/trustpilot-setup' },
        { name: 'Capterra Monitoring', href: '/docs/capterra-setup' },
        { name: 'Website Widget', href: '/docs/widget-setup' },
      ]
    },
    {
      title: 'Features',
      icon: Settings,
      items: [
        { name: 'Ticket Management', href: '/docs/ticket-management' },
        { name: 'Team Collaboration', href: '/docs/team-collaboration' },
        { name: 'AI Response Polish', href: '/docs/ai-responses' },
        { name: 'Review Monitoring', href: '/docs/review-monitoring' },
        { name: 'Workspaces', href: '/docs/workspaces' },
        { name: 'Automation', href: '/docs/automation' },
      ]
    },
    {
      title: 'FAQ & Troubleshooting',
      icon: HelpCircle,
      items: [
        { name: 'Common Issues', href: '/docs/troubleshooting' },
        { name: 'Billing & Plans', href: '/docs/billing-faq' },
        { name: 'Account Management', href: '/docs/account-faq' },
      ]
    },
    {
      title: 'API Reference',
      icon: Code,
      items: [
        { name: 'Authentication', href: '/docs/api-auth' },
        { name: 'Endpoints', href: '/docs/api-endpoints' },
        { name: 'Webhooks', href: '/docs/api-webhooks' },
      ]
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Top Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden mr-4 p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
              >
                {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
              <a href="/" className="flex items-center">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
                  <MessageCircle className="w-4 h-4 text-white" />
                </div>
                <span className="ml-3 text-xl font-semibold text-gray-900">TicketDesk</span>
                <span className="ml-3 text-sm text-gray-500">/ Documentation</span>
              </a>
            </div>

            <div className="flex items-center space-x-4">
              <a href="/" className="text-sm text-gray-700 hover:text-gray-900">
                ← Back to Home
              </a>
              <a href="/login" className="text-sm text-gray-700 hover:text-gray-900 hidden sm:block">
                Sign In
              </a>
              <a href="/register" className="bg-purple-600 text-white px-4 py-2 rounded text-sm font-medium hover:bg-purple-700">
                Try Free
              </a>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Sidebar Navigation */}
          <aside className={`
            ${sidebarOpen ? 'block' : 'hidden'}
            lg:block w-64 flex-shrink-0
            fixed lg:sticky top-20 left-0 h-screen lg:h-auto
            bg-white lg:bg-transparent
            border-r lg:border-0 border-gray-200
            z-40 overflow-y-auto
            p-4 lg:p-0
          `}>
            {/* Search */}
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search docs..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>

            {/* Navigation */}
            <nav className="space-y-6">
              {navigation.map((section) => {
                const Icon = section.icon;
                return (
                  <div key={section.title}>
                    <div className="flex items-center text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                      <Icon className="w-4 h-4 mr-2" />
                      {section.title}
                    </div>
                    <ul className="space-y-2">
                      {section.items.map((item) => (
                        <li key={item.name}>
                          <a
                            href={item.href}
                            className={`
                              block px-3 py-2 rounded-md text-sm transition-colors
                              ${currentPage === item.href
                                ? 'bg-purple-50 text-purple-700 font-medium'
                                : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                              }
                            `}
                          >
                            {item.name}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </nav>

            {/* Help Section */}
            <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Need Help?</h4>
              <p className="text-xs text-gray-600 mb-3">Can't find what you're looking for?</p>
              <a
                href="mailto:hello@ticketdesk.app"
                className="text-xs text-blue-600 hover:text-blue-700 font-medium"
              >
                Contact Support →
              </a>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            <div className="max-w-3xl">
              {children}
            </div>

            {/* Footer Navigation */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <a href="#" className="text-sm text-gray-600 hover:text-gray-900">
                  ← Previous: Getting Started
                </a>
                <a href="#" className="text-sm text-gray-600 hover:text-gray-900">
                  Next: Gmail Setup →
                </a>
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-gray-600 bg-opacity-50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default DocsLayout;
